import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-dropdown/style.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import CalendarIcon from "./images/Calendar.png";
import Prescription from "../../../assets/Prescription.png";
import Pathology from "../../../assets/Pathology Reports.png";
import Hospital from "../../../assets/Hospital Records.png";
import Medical from "../../../assets/Medical Images.png";
import Vaccination from "../../../assets/Vaccination.png";
import * as utils from "../../../Utils";
import "./History.css";
import { Link } from "react-router-dom";
import { useApp } from "./../../../AppProvider";
import api from "../../../axiosInstance";

const options = [
  { value: "PathologyRecord", label: "PathologyRecord" },
  { value: "RadiologyRecord", label: "RadiologyRecord" },
  { value: "VaccinationRecord", label: "VaccinationRecord" },
  { value: "PrescriptionRecord", label: "PrescriptionRecord" },
  { value: "HospitalizationRecord", label: "HospitalizationRecord" },
];

const logos = {
  PrescriptionRecord: Prescription,
  PathologyRecord: Pathology,
  RadiologyRecord: Medical,
  VaccinationRecord: Vaccination,
  HospitalizationRecord: Hospital,
};

const RecordCard = ({ record }) => {
  const logo = logos[record.typeOfRecord] || Pathology;

  return (
    <Link className="history__card_container" to={`/DashboardDashboardNavigation/records/${record.recordId}`}>
      <div className="history__inner-card">
        <div className="icon">
          <img className="icon-logo" src={logo} alt="logo" />
          <div className="date-tag">
            <span>{utils.formatDate(record.dateOfPublish)}</span>
          </div>
        </div>
        <div className="card-detail">
          <h4>{record.typeOfRecord}</h4>
          <ul className="ul">
            {/* Pathology */}
            {record.typeOfRecord === "PathologyRecord" && record.testValues?.filter((item) => item.classification === "Abnormal")
              .slice(0, 3).map((item, index) => (
                <li key={index}>{item.testName}: <span className="detail1">{item.value}</span></li>
              ))}
            {/* Radiology */}
            {record.typeOfRecord === "RadiologyRecord" && (
              <>
                {record.typeOfRadiologyRecord &&
                  <li>Radiology Type: <span className="detail1">{record.typeOfRadiologyRecord}</span></li>}
                {record.diagnosedArea?.length > 0 &&
                  <li>Diagnosed Area: <span className="detail1">{record.diagnosedArea.join(", ")}</span></li>}
                {record.symptoms?.length > 0 &&
                  <li>symptoms: <span className="detail1">{record.symptoms.slice(0, 2).join(", ")}</span></li>}
              </>
            )}
            {/* Prescription */}
            {record.typeOfRecord === "PrescriptionRecord" && record.medications?.slice(0, 4).map((item, index) => (
              <li key={index} className="history__prescription-medicine">
                {index + 1}. {item.drugsName}
              </li>
            ))}
          </ul>

          {/* Diagnosis capsules */}
          {record.diagnosis?.length > 0 && (
            <div className="history__diagnosis-capsule-container">
              {record.diagnosis.map((item, index) =>
                item
                  ? <span key={index} className="history__diagnosis-capsule">{item}</span>
                  : null
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

const PatientSummaryCard = ({ patientId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    api.post(`/medical-summary/${patientId}`)
      .then((res) => setSummary(res.data))
      .catch(() => setError("Unable to load patient summary."))
      .finally(() => setLoading(false));
  }, [patientId]);

  if (loading) {
    return (
      <div className="history__summary-card history__summary-card--loading">
        <div className="history__summary-shimmer" />
      </div>
    );
  }

  if (error || !summary) return null;

  const generatedAt = summary.generatedAt
    ? new Date(summary.generatedAt).toLocaleString()
    : null;

  return (
    <div className="history__summary-card">
      <div className="history__summary-header">
        <div className="history__summary-title-row">
          <span className="history__summary-icon">&#10024;</span>
          <h3 className="history__summary-title">AI Patient Summary</h3>
          {summary.cached && <span className="history__summary-badge">Cached</span>}
        </div>
        {generatedAt && (
          <span className="history__summary-meta">Generated {generatedAt}</span>
        )}
      </div>
      <p className="history__summary-text">{summary.summary}</p>
    </div>
  );
};

function History() {
  const { patientsMHR, setBoardDate, selectedPatient } = useApp();
  const [filteredData, setFilteredData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [condition, setCondition] = useState("");

  useEffect(() => {
    let filtered = [...patientsMHR];

    if (condition) {
      filtered = filtered.filter((r) => r.typeOfRecord === condition);
    }
    if (startDate && endDate) {
      filtered = filtered.filter((r) => {
        const d = new Date(r.dateOfPublish);
        return d >= startDate && d <= endDate;
      });
    }

    filtered.sort((a, b) => new Date(b.dateOfPublish) - new Date(a.dateOfPublish));
    setFilteredData(filtered);
  }, [condition, patientsMHR, startDate, endDate]);

  const handleDateChange = (start, end) => {
    setStartDate(new Date(start));
    setEndDate(new Date(end));
    setBoardDate(new Date(start).toISOString().split("T")[0]);
  };

  return (
    <div className="history-details">
      <div className="history-dates">
        <Select
          className="react-select-container"
          classNamePrefix="react-select"
          options={options}
          value={options.find((o) => o.value === condition) || null}
          onChange={(option) => setCondition(option ? option.value : "")}
          placeholder="Select Condition"
          isClearable
        />

        <div className="history__select-date">
          <span className="date-range-label">
            {startDate && endDate
              ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
              : "Select Date Range"}
          </span>
          <DateRangePicker
            onApply={(_, picker) => handleDateChange(picker.startDate, picker.endDate)}
            onCancel={() => { setStartDate(null); setEndDate(null); }}
            opens="right"
            autoApply
            autoUpdateInput={false}
            linkedCalendars={false}
            months={1}
            showCustomRangeLabel={false}
            locale={{ cancelLabel: "Clear" }}
          >
            <img className="icon-logo" src={CalendarIcon} alt="calendar" />
          </DateRangePicker>
        </div>
      </div>

      <PatientSummaryCard patientId={selectedPatient} />

      <div className="history-cards">
        {filteredData.length > 0
          ? filteredData.map((record) => (
            <RecordCard key={record.recordId} record={record} />
          ))
          : <div className="history__empty">No history records available for this patient yet.<br />Records will appear here once added.</div>}
      </div>
    </div>
  );
}

export default History;